import { logger } from '../../utils/logger';
import { SapUiNavigationEvent } from './types';
import { postToSapHost } from '../sap-context/cti-origin.util';
import { getSapC4cBaseUrl } from '../../utils/config';

function getSapUiRoutingKey(contactType: 'individual' | 'corporate'): string {
  return contactType === 'corporate' ? 'mdaccount' : 'mdcontact';
}

export function navigateSapContactViaUiEvent(
  contactId: string,
  contactType: 'individual' | 'corporate' = 'individual'
): void {
  const message: SapUiNavigationEvent = {
    operation: 'navigation',
    params: {
      objectKey: contactId,
      routingKey: getSapUiRoutingKey(contactType),
      viewType: 'details',
    },
  };

  logger.debug('[SAP UI] Navigating via UI Event:', message);
  postToSapHost(message);
}

export function getSapBaseUrl(): string | null {
  const overrides = (window as { overrides_data?: { features?: { sapC4cBaseUrl?: string } } })
    .overrides_data;
  if (overrides?.features?.sapC4cBaseUrl) {
    return overrides.features.sapC4cBaseUrl;
  }

  const config = (window as { __APP_CONFIG__?: { SAP_C4C_BASE_URL?: string } }).__APP_CONFIG__;
  if (config?.SAP_C4C_BASE_URL) {
    return config.SAP_C4C_BASE_URL;
  }

  const fromFeatures = getSapC4cBaseUrl()?.trim();
  if (fromFeatures) {
    return fromFeatures;
  }

  try {
    const parentOrigin = window.parent.location.origin;
    if (parentOrigin.includes('crm.cloud.sap') || parentOrigin.includes('c4c')) {
      return parentOrigin;
    }
  } catch {
    // Cross-origin access blocked when embedded
  }

  return null;
}

/**
 * Build a Sales/Service Cloud V2 deep link for a contact or account.
 * @see Routing Keys for Entities — /go/detail/{routingKey}?nodeid=
 */
export function buildSapContactDeepLink(
  contactId: string,
  contactType: 'individual' | 'corporate',
  sapBaseUrl: string
): string {
  const base = sapBaseUrl.replace(/\/$/, '');
  const routingKey = getSapUiRoutingKey(contactType);
  return `${base}/go/detail/${routingKey}?nodeid=${encodeURIComponent(contactId)}`;
}

/**
 * Open a contact/account via SAP deep link in a new tab.
 * Prefer browseSapContact / navigateSapContactViaUiEvent when embedded in SAP
 * so the Agent Desktop shell navigates in-place without a new browser tab.
 */
export function openSapContactInNewTab(
  contactId: string,
  contactType: 'individual' | 'corporate',
  sapBaseUrl?: string
): void {
  const baseUrl = sapBaseUrl || getSapBaseUrl();

  if (!baseUrl) {
    logger.error('[SAP CTI] Cannot open contact - SAP base URL not configured');
    return;
  }

  const url = buildSapContactDeepLink(contactId, contactType, baseUrl);
  logger.debug('[SAP CTI] Opening contact via deep link:', url);
  window.open(url, '_blank', 'noopener,noreferrer');
}
